import PropTypes from "prop-types"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import { SharedBillingContext } from "src/main/Billing/SharedBillingContext"

import Button from "src/components/Button"
import Loader from "src/components/Loader"

import { getWorkOrder, updateAutopay } from "src/api/Service/WorkOrders"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { inchesToFeet } from "src/utils/UnitConversion"

import AutopaySection from "./AutopaySection"
import BillingSection from "./BillingSection"
import BoatSection from "./BoatSection"
import ContactSection from "./ContactSection"
import DatesSection from "./DatesSection"
import JobsSection from "./JobsSection"
import WorkOrderPrintMount from "./PrintDocuments/WorkOrderPrintMount"
import PrintWorkOrderModal from "./PrintWorkOrderModal"
import RequestDescriptionSection from "./RequestDescriptionSection"
import ScheduleTrigger from "./ScheduleTrigger"
import UpdateAutopayModal from "./UpdateAutopayModal"
import WorkOrderActions from "./WorkOrderActions"
import WorkOrderHeader from "./WorkOrderHeader"
import { DETAIL_PAGE_VIEWED } from "./amplitude_events"

const STATUS_STYLE = {
  open: {
    bg: "bg-gray-200",
    accent: "border-l-4 border-gray-500",
    text: "text-gray-900",
  },
  in_progress: {
    bg: "bg-blue-100",
    accent: "border-l-4 border-blue-500",
    text: "text-blue-900",
  },
  work_complete: {
    bg: "bg-supplementary-violet-100",
    accent: "border-l-4 border-supplementary-violet-500",
    text: "text-supplementary-violet-900",
  },
  closed: {
    bg: "bg-teal-100",
    accent: "border-l-4 border-teal-500",
    text: "text-teal-900",
  },
}

const WorkOrder = ({
  workOrderSequentialId,
  cardReaders = [],
  enableBluetoothCardReader = false,
}) => {
  const { marinaSlug } = useMarina()
  const queryClient = useQueryClient()
  const showToast = useToast()
  const tracker = useTracker()
  const [isAutopayModalOpen, setIsAutopayModalOpen] = useState(false)
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false)
  const [printVersion, setPrintVersion] = useState(null)

  const {
    data: workOrder,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["workOrder", marinaSlug, workOrderSequentialId],
    queryFn: () => getWorkOrder(marinaSlug, workOrderSequentialId),
  })

  const { mutate: submitAutopay, isLoading: isSubmittingAutopay } = useMutation(
    (values) => updateAutopay(marinaSlug, workOrderSequentialId, values),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "workOrder",
          marinaSlug,
          workOrderSequentialId,
        ])
        queryClient.invalidateQueries([
          "workOrderInvoices",
          marinaSlug,
          workOrderSequentialId,
        ])
        setIsAutopayModalOpen(false)
        showToast("AutoPay updated.", { type: "success" })
      },
      onError: (error) => {
        showToast(error.message || "Failed to update AutoPay.", {
          type: "error",
        })
      },
    }
  )

  const detailViewLogged = useRef(false)
  useEffect(() => {
    if (!workOrder || detailViewLogged.current) return
    detailViewLogged.current = true
    tracker.trackEvent(DETAIL_PAGE_VIEWED, {
      work_order_id: workOrderSequentialId,
      jobs_count: workOrder.jobs?.length ?? 0,
      derived_status: workOrder.status,
    })
  }, [workOrder, workOrderSequentialId, tracker])

  // Settle reads readers from context. Only the slice — the full provider is
  // reservation-shaped and would fire reservation queries here.
  const sharedBilling = useMemo(
    () => ({ cardReaders, enableBluetoothCardReader }),
    [cardReaders, enableBluetoothCardReader]
  )

  if (isLoading) return <Loader name="work order" />
  if (isError)
    return (
      <div className="mx-auto max-w-[860px] px-2 pb-2 lg:px-8 lg:py-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-center text-sm text-red-700">
          Something went wrong loading this work order. Please try refreshing
          the page.
        </div>
      </div>
    )

  const boatLengthFeet = workOrder.contactBoat?.lengthOverall
    ? inchesToFeet(workOrder.contactBoat.lengthOverall)
    : null

  const statusStyle = STATUS_STYLE[workOrder.status] || STATUS_STYLE.open
  const isClosed = workOrder.status === "closed"

  return (
    <div className="mx-auto px-4 pb-4 xl:max-w-[1500px]">
      <WorkOrderHeader
        workOrder={workOrder}
        utilityActions={
          workOrder.serviceBillingEnabled && (
            <Button
              variant="secondary"
              icon="fa-print"
              onClick={() => setIsPrintModalOpen(true)}
            >
              Print
            </Button>
          )
        }
      />
      <div className="mb-6 overflow-hidden rounded-lg border">
        <div
          className={`flex items-center justify-between gap-4 px-5 py-3 ${statusStyle.bg} ${statusStyle.accent}`}
        >
          <div className="flex flex-col">
            <span className={`font-semibold ${statusStyle.text}`}>
              {workOrder.statusLabel}
            </span>
            {isClosed && (
              <span className="mt-0.5 text-xs text-gray-700">
                This work order is closed. Reopen to make changes.
              </span>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {workOrder.status === "open" && (
              <ScheduleTrigger
                workOrder={workOrder}
                workOrderSequentialId={workOrderSequentialId}
              />
            )}
            <WorkOrderActions
              workOrder={workOrder}
              workOrderSequentialId={workOrderSequentialId}
            />
          </div>
        </div>
        <div className="grid grid-cols-12 gap-6 p-5">
          <div className="col-span-12 lg:col-span-6">
            <ContactSection contact={workOrder.contact} />
          </div>
          <div className="col-span-12 lg:col-span-6">
            <BoatSection
              boat={workOrder.contactBoat}
              assignedSpaceName={workOrder.assignedSpaceName}
            />
          </div>
          <div className="col-span-12">
            <DatesSection
              createdAt={workOrder.createdAtFormatted}
              scheduledAt={workOrder.scheduledAtFormatted}
              workStartedAt={workOrder.workStartedAtFormatted}
              closedAt={workOrder.closedAtFormatted}
              timeToClose={workOrder.timeToCloseLabel}
            />
          </div>
        </div>
      </div>
      {workOrder.requestDescription && (
        <div className="mb-6 rounded-lg border p-5">
          <RequestDescriptionSection
            requestDescription={workOrder.requestDescription}
          />
        </div>
      )}
      <JobsSection
        jobs={workOrder.jobs || []}
        workOrderSequentialId={workOrderSequentialId}
        boatLengthFeet={boatLengthFeet}
        isClosed={isClosed}
      />
      <AutopaySection
        workOrder={workOrder}
        onManage={() => setIsAutopayModalOpen(true)}
      />
      <SharedBillingContext.Provider value={sharedBilling}>
        <BillingSection
          workOrder={workOrder}
          workOrderSequentialId={workOrderSequentialId}
        />
      </SharedBillingContext.Provider>
      <UpdateAutopayModal
        isOpen={isAutopayModalOpen}
        onClose={() => setIsAutopayModalOpen(false)}
        onSubmit={submitAutopay}
        isSubmitting={isSubmittingAutopay}
        workOrder={workOrder}
        workOrderSequentialId={workOrderSequentialId}
      />
      <PrintWorkOrderModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        workOrder={workOrder}
        onPrint={setPrintVersion}
      />
      {printVersion && (
        <WorkOrderPrintMount
          version={printVersion}
          workOrderSequentialId={workOrderSequentialId}
          onDone={() => setPrintVersion(null)}
        />
      )}
    </div>
  )
}

WorkOrder.propTypes = {
  workOrderSequentialId: PropTypes.string.isRequired,
  cardReaders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      displayName: PropTypes.string,
    })
  ),
  enableBluetoothCardReader: PropTypes.bool,
}

export default WorkOrder
